import React, { useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    InputAdornment,
    Box,
    MenuItem,
    FormControl,
    Select,
    Typography,
    IconButton
} from '@mui/material';
import { LoadingButton } from "@mui/lab";
import {
    TitleOutlined,
    LocationOnOutlined,
    HouseOutlined,
    StairsOutlined,
    ApartmentOutlined,
    FlagOutlined,
    LocationCity,
    PinDropOutlined,
    LocalPostOfficeOutlined,
    HomeOutlined,
    DescriptionOutlined,
    ErrorOutline,
    NotesOutlined,
    WorkOutline,
    GroupOutlined,
    AddCircleOutline
} from "@mui/icons-material";
import { t } from 'i18next';
import { getLocalStorageMenuItem } from '../../utility';
import NewLocationPicker from './NewLocationPicker';



const LocationFormDialog = ({
    open,
    onClose,
    isEditDelivery,
    isProcessLoading,
    locationFormData,
    handleLocationFormInputChange,
    handleSaveLocation,
    handleUpdateLocation,
    setMobileBackColor,
    setMobileTextColor,
    setMobileCardBorderColor,
    setMobileCardColor,
    setMobileButttonBackColor
}) => {

    const [mapOpen, setMapOpen] = React.useState(false);

    const handleOpenMap = () => setMapOpen(true);
    const handleCloseMap = () => setMapOpen(false);

    const handleLatLngUpdate = (coords) => {
        handleLocationFormInputChange({
            target: { name: "latitude", value: String(coords.lat) }
        });
        handleLocationFormInputChange({
            target: { name: "longitude", value: String(coords.lng) }
        });
        handleCloseMap();
    };

    const LOCATION_TYPES = [
        { value: 'home', label: t("Home1"), icon: <HomeOutlined /> },
        { value: 'work', label: t("Work"), icon: <WorkOutline /> },
    ];

    useEffect(() => {
        if (open && !locationFormData.title) {
            handleLocationFormInputChange({
                target: { name: "title", value: t("Home1") }
            });
        }
    }, [open]);
    // Check which required fields are empty
    const requiredFields = [
        'street',
        'country',
        'city',
        'postcode'
    ];


    const emptyFields = requiredFields.filter(
        field => !locationFormData[field] || locationFormData[field].trim() === ''
    );

    return (
        <Dialog
            className="alignByLang"
            disableEscapeKeyDown
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            sx={{
                backgroundColor: setMobileBackColor(),
                color: setMobileTextColor(),
                border: `1px solid ${setMobileCardBorderColor()}`,
            }}
            PaperProps={{
                style: {
                    backgroundColor: setMobileBackColor(),
                    color: setMobileTextColor(),
                    border: `1px solid ${setMobileCardBorderColor()}`,
                },
            }}
        >
            <DialogTitle className="alignByLang head-font">🏠 {t("Enter Location Details")}</DialogTitle>
            <DialogContent dividers className="alignByLang sub-font">
                <Box className="alignByLang" display="flex" flexDirection="column" gap={2} mt={1}>
                    {/* Location Type Icons */}
                    <Typography variant="body2" sx={{ mb: 1 }}>{t("Select Location Type")}</Typography>
                    <Box sx={{ justifyContent: "center" }} display="flex" gap={0.5} >
                        {LOCATION_TYPES.map((type) => (
                            <Box
                                key={type.value}
                                onClick={() =>
                                    handleLocationFormInputChange({ target: { name: "title", value: type.label } })
                                }
                                sx={{
                                    borderRadius: 2,
                                    border: `2px solid ${locationFormData.title === type.label ? setMobileButttonBackColor() : setMobileCardBorderColor()}`,
                                    backgroundColor: locationFormData.title === type.label ? setMobileButttonBackColor() + "22" : setMobileCardColor(),
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexDirection: "column",
                                    width: "30%",
                                    paddingInline: 1,
                                    "&:hover": { backgroundColor: setMobileButttonBackColor() + "33" }
                                }}
                            >
                                {React.cloneElement(type.icon, { sx: { color: setMobileTextColor(), fontSize: 20 } })}
                                <Typography style={{ textAlign: "center" }} variant="caption">{type.label}</Typography>
                            </Box>
                        ))}

                        {/* Other option */}
                        <Box
                            onClick={() => handleLocationFormInputChange({ target: { name: "title", value: "" } })}
                            sx={{
                                borderRadius: 2,
                                border: `2px dashed ${setMobileCardBorderColor()}`,
                                backgroundColor: setMobileCardColor(),
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexDirection: "column",
                                width: "30%",
                                paddingInline: 1
                            }}
                        >
                            <AddCircleOutline sx={{ color: setMobileTextColor(), fontSize: 20 }} />
                            <Typography style={{ textAlign: "center" }} variant="caption">{t("Other")}</Typography>
                        </Box>
                    </Box>

                    {/* Location Type TextField */}
                    <TextField
                        name="title"
                        value={locationFormData.title || ""}
                        onChange={handleLocationFormInputChange}
                        fullWidth
                        placeholder={t("Type location type")}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <TitleOutlined sx={{ color: setMobileTextColor() }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            color: setMobileTextColor(),
                            backgroundColor: setMobileCardColor(),
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: setMobileButttonBackColor(),
                            },
                            "& .MuiInputBase-input": {
                                color: setMobileTextColor(),
                                textAlign: "center",
                            },
                        }}
                    />



                    {/* Highlight empty required fields */}
                    {emptyFields.length > 0 && (
                        <Box sx={{
                            display: 'none',
                            alignItems: 'center',
                            gap: 1,
                            color: 'warning.main',
                            backgroundColor: 'rgba(255, 152, 0, 0.1)',
                            p: 1,
                            borderRadius: 1,
                        }}>
                            <ErrorOutline fontSize="small" />
                            <Typography variant="body2">
                                {t("Please complete the highlighted fields")}
                            </Typography>
                        </Box>
                    )}

                    {/* Address Fields */}
                    {requiredFields.map((field) => {
                        const icons = {
                            street: <LocationOnOutlined sx={{ color: setMobileTextColor() }} />,
                            country: <FlagOutlined sx={{ color: setMobileTextColor() }} />,
                            city: <LocationCity sx={{ color: setMobileTextColor() }} />,
                            postcode: <LocalPostOfficeOutlined sx={{ color: setMobileTextColor() }} />,
                        };

                        const labels = {
                            street: t("Street Name"),
                            country: t("Country"),
                            city: t("City"),
                            postcode: t("Postal Code"),
                        };

                        return (
                            <TextField
                                key={field}
                                name={field}
                                value={locationFormData[field] || ''}
                                onChange={handleLocationFormInputChange}
                                fullWidth
                                placeholder={labels[field]}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            {icons[field]}
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    display: "none",
                                    color: setMobileTextColor(),
                                    backgroundColor: setMobileCardColor(),
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: emptyFields.includes(field)
                                            ? 'warning.main'
                                            : setMobileCardBorderColor(),
                                    },
                                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: setMobileButttonBackColor(),
                                    },
                                    "& .MuiInputBase-input": {
                                        color: setMobileTextColor(),
                                        textAlign: "center",
                                        "&::placeholder": {
                                            color: setMobileTextColor(),
                                            opacity: 0.7,
                                            fontFamily:
                                                getLocalStorageMenuItem("lang") === "ar" ||
                                                    getLocalStorageMenuItem("lang") === "fa"
                                                    ? "Almarai"
                                                    : "Roboto, sans-serif",
                                        },
                                    },
                                }}
                            />
                        );
                    })}

                    <TextField
                        name={"unit_number"}
                        value={locationFormData["unit_number"] || ''}
                        onChange={handleLocationFormInputChange}
                        fullWidth
                        placeholder={t("Unit No")}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <HouseOutlined sx={{ color: setMobileTextColor() }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            display: "none",
                            color: setMobileTextColor(),
                            backgroundColor: setMobileCardColor(),
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: setMobileButttonBackColor(),
                            },
                            "& .MuiInputBase-input": {
                                color: setMobileTextColor(),
                                textAlign: "center",
                                "&::placeholder": {
                                    color: setMobileTextColor(),
                                    opacity: 0.7,
                                    fontFamily:
                                        getLocalStorageMenuItem("lang") === "ar" ||
                                            getLocalStorageMenuItem("lang") === "fa"
                                            ? "Almarai"
                                            : "Roboto, sans-serif",
                                },
                            },
                        }}
                    />

                    {/* Optional Fields */}
                    <TextField

                        name="state"
                        value={locationFormData.state || ''}
                        onChange={handleLocationFormInputChange}
                        fullWidth
                        placeholder={t("State")}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <PinDropOutlined sx={{ color: setMobileTextColor() }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            display: "none",
                            color: setMobileTextColor(),
                            backgroundColor: setMobileCardColor(),
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: setMobileCardBorderColor(),
                            },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: setMobileButttonBackColor(),
                            },
                            "& .MuiInputBase-input": {
                                color: setMobileTextColor(),
                                textAlign: "center",
                                "&::placeholder": {
                                    color: setMobileTextColor(),
                                    opacity: 0.7,
                                    fontFamily:
                                        getLocalStorageMenuItem("lang") === "ar" ||
                                            getLocalStorageMenuItem("lang") === "fa"
                                            ? "Almarai"
                                            : "Roboto, sans-serif",
                                },
                            },
                        }}
                    />

                    <TextField
                        name="province"
                        value={locationFormData.province || ''}
                        onChange={handleLocationFormInputChange}
                        fullWidth
                        placeholder={t("Province")}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <FlagOutlined sx={{ color: setMobileTextColor() }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            display: "none",
                            color: setMobileTextColor(),
                            backgroundColor: setMobileCardColor(),
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: setMobileCardBorderColor(),
                            },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: setMobileButttonBackColor(),
                            },
                            "& .MuiInputBase-input": {
                                color: setMobileTextColor(),
                                textAlign: "center",
                                "&::placeholder": {
                                    color: setMobileTextColor(),
                                    opacity: 0.7,
                                    fontFamily:
                                        getLocalStorageMenuItem("lang") === "ar" ||
                                            getLocalStorageMenuItem("lang") === "fa"
                                            ? "Almarai"
                                            : "Roboto, sans-serif",
                                },
                            },
                        }}
                    />

                    <TextField
                        name="suburb"
                        value={locationFormData.suburb || ''}
                        onChange={handleLocationFormInputChange}
                        fullWidth
                        placeholder={t("Suburb")}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <HomeOutlined sx={{ color: setMobileTextColor() }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            display: "none",
                            color: setMobileTextColor(),
                            backgroundColor: setMobileCardColor(),
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: setMobileCardBorderColor(),
                            },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: setMobileButttonBackColor(),
                            },
                            "& .MuiInputBase-input": {
                                color: setMobileTextColor(),
                                textAlign: "center",
                                "&::placeholder": {
                                    color: setMobileTextColor(),
                                    opacity: 0.7,
                                    fontFamily:
                                        getLocalStorageMenuItem("lang") === "ar" ||
                                            getLocalStorageMenuItem("lang") === "fa"
                                            ? "Almarai"
                                            : "Roboto, sans-serif",
                                },
                            },
                        }}
                    />

                    <TextField
                        name="description"
                        value={locationFormData.description || ''}
                        onChange={handleLocationFormInputChange}
                        fullWidth
                        placeholder={t("Building Details - Floor - Apartment Number")}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <DescriptionOutlined sx={{ color: setMobileTextColor() }} />
                                </InputAdornment>
                            ),
                            endAdornment: isEditDelivery && (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleOpenMap}>
                                        <PinDropOutlined color="primary" />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                        sx={{
                            color: setMobileTextColor(),
                            backgroundColor: setMobileCardColor(),
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: setMobileCardBorderColor(),
                            },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: setMobileButttonBackColor(),
                            },
                            "& .MuiInputBase-input": {
                                color: setMobileTextColor(),
                                textAlign: "center",
                            },
                        }}
                    />
                    <TextField
                        name="additional_notes"
                        value={locationFormData.additional_notes || ''}
                        onChange={handleLocationFormInputChange}
                        fullWidth
                        placeholder={t("Delivery Notes - Distinctive Mark")}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <NotesOutlined sx={{ color: setMobileTextColor() }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            color: setMobileTextColor(),
                            backgroundColor: setMobileCardColor(),
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: setMobileCardBorderColor(),
                            },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: setMobileButttonBackColor(),
                            },
                            "& .MuiInputBase-input": {
                                color: setMobileTextColor(),
                                textAlign: "center",
                                "&::placeholder": {
                                    color: setMobileTextColor(),
                                    opacity: 0.7,
                                    fontFamily:
                                        getLocalStorageMenuItem("lang") === "ar" ||
                                            getLocalStorageMenuItem("lang") === "fa"
                                            ? "Almarai"
                                            : "Roboto, sans-serif",
                                },
                            },
                        }}
                    />
                </Box>
            </DialogContent>
            <DialogActions className="alignByLang" sx={{ flexDirection: "row", justifyContent: "space-around" }}>
                <Button
                    className="head-font"
                    sx={{
                        backgroundColor: setMobileBackColor(),
                        color: setMobileTextColor(),
                        border: `1px solid ${setMobileCardBorderColor()}`,
                        "&.Mui-disabled": {
                            color: setMobileTextColor(),
                        }
                    }}
                    onClick={onClose}
                >
                    {t("Cancel")}
                </Button>
                <LoadingButton
                    className="head-font"
                    sx={{
                        backgroundColor: setMobileBackColor(),
                        color: setMobileTextColor(),
                        border: `1px solid ${setMobileCardBorderColor()}`,
                        "&.Mui-disabled": {
                            color: setMobileTextColor(),
                        }
                    }}
                    onClick={isEditDelivery ? handleUpdateLocation : handleSaveLocation}
                    loading={isProcessLoading}
                    disabled={
                        !locationFormData.title ||
                        isProcessLoading
                    }
                >
                    {isEditDelivery ? t("Update Location") : t("Save Location")}
                </LoadingButton>
            </DialogActions>


            {mapOpen && (
                <NewLocationPicker
                    closeMapModal={handleCloseMap}
                    openLocationFormDailog={() => { }}
                    is_from_update={true}
                    latitude={locationFormData?.latitude || null}
                    longitude={locationFormData?.longitude || null}
                    latlangVal={handleLatLngUpdate}
                    updateLocationFormData={(addr) => {
                        const place = {
                            street: addr.road || "",
                            unit_number: addr.house_number || "",
                            floor_number: locationFormData.floor_number || "",
                            apartment_number: locationFormData.apartment_number || "",
                            suburb: addr.suburb || addr.neighbourhood || "",
                            city: addr.city || addr.town || addr.village || "",
                            province: addr.state || "",
                            state: addr.state || "",
                            postcode: addr.postcode || "",
                            country: addr.country || ""
                        };

                        // Update form fields individually
                        Object.keys(place).forEach((key) => {
                            handleLocationFormInputChange({ target: { name: key, value: place[key] } });
                        });

                        // Build description from structured fields
                        const description = `${t("Street")} ${place.street}, ${t("Unit No")} ${place.unit_number}, ${t("Floor No")} ${place.floor_number}, ${t("Apartment No")} ${place.apartment_number}`;

                        handleLocationFormInputChange({
                            target: { name: "description", value: description }
                        });

                    }}
                />
            )}

        </Dialog>
    );
};

export default LocationFormDialog;